import { ChangeEventHandler, FC, useMemo } from 'react';

import dayjs from 'dayjs';
import useSWR from 'swr';

import Chip from '@mui/material/Chip';

import { CheaterActionsColumn } from './cheaterTableColumns/CheaterActionsColumn';
import { CheaterScriptColumn } from './cheaterTableColumns/CheaterScriptColumn';

import css from './CheatersPage.module.css';

import { userService } from 'v2/api/userService';
import { DataTable } from 'v2/components/dataTable/DataTable';
import { PageLayout } from 'v2/components/pageLayout/PageLayout';
import { PageTitle } from 'v2/components/pageLayout/pageTitle/PageTitle';
import { SearchTextField } from 'v2/components/searchTextField/SearchTextField';
import { useFilter } from 'v2/hooks/useFilter';
import { usePagination } from 'v2/hooks/usePagination';
import { TableColumn } from 'v2/types';
import { CheaterModel, CheatersFilter } from 'v2/types/CheaterModel';

export const CheatersPage: FC = () => {
  const { page, pageSize, onPageChange } = usePagination();

  const { filter, debouncedFilter, onChange } = useFilter<CheatersFilter>({ userNickname: '' });

  const { data, mutate } = useSWR(['getCheaters', page, pageSize, debouncedFilter], userService.getCheaters, {
    keepPreviousData: true,
  });

  const columns: TableColumn<CheaterModel>[] = useMemo(
    () => [
      { id: 'userNickname', key: 'userNickname', label: 'User Nickname', ellipsis: true },
      {
        key: 'date',
        label: 'Date',
        render: (row) => dayjs(row.date).format('DD.MM.YYYY HH:mm'),
      },
      {
        key: 'script',
        label: 'Script',
        render: (row) => <CheaterScriptColumn cheater={row} />,
      },
      {
        key: 'wasBanned',
        label: 'User Was Banned',
        align: 'center',
        render: (row) => (
          <Chip
            color={row.wasBanned ? 'error' : 'info'}
            label={row.wasBanned ? 'Was Banned' : 'Not Banned'}
            size="small"
          />
        ),
      },
      {
        key: 'actions',
        align: 'right',
        render: (row) => <CheaterActionsColumn cheater={row} onRevalidate={mutate} />,
      },
    ],
    [mutate]
  );

  const handleChangeFilter: ChangeEventHandler<HTMLInputElement> = (e) => {
    onChange('userNickname', e.target.value);
  };

  return (
    <PageLayout className={css.cheatersPage} overflow="hidden">
      <PageTitle
        action={<SearchTextField label="Cheater Name" value={filter.userNickname} onChange={handleChangeFilter} />}
      >
        Cheaters
      </PageTitle>

      <DataTable
        columns={columns}
        data={data?.result}
        page={page}
        total={data?.total}
        pageSize={pageSize}
        onPageChange={onPageChange}
        withPagination
      />
    </PageLayout>
  );
};
